import React from 'react';
import { motion } from 'framer-motion';
import { Zap, Shield, Globe, Headphones, Gamepad2, Tv, Building2, Wifi } from 'lucide-react';

const ServicesSection = () => {
  const services = [
    {
      icon: Zap,
      title: 'Kecepatan Tinggi',
      description: 'Nikmati internet fiber optik hingga 1 Gbps untuk semua kebutuhan digital Anda.',
      color: 'blue',
      size: 'large',
      image: 'https://images.unsplash.com/photo-1768141728185-c14bdfe90f91?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNzl8MHwxfHNlYXJjaHw0fHxnYW1lciUyMHBsYXlpbmclMjBjb21wdXRlciUyMGRhcmslMjByb29tJTIwbmVvbnxlbnwwfHx8fDE3NzI1NDg0OTZ8MA&ixlib=rb-4.1.0&q=85'
    },
    {
      icon: Shield,
      title: 'Jaringan Aman',
      description: 'Proteksi keamanan tingkat enterprise untuk data Anda.',
      color: 'red',
      size: 'normal'
    },
    {
      icon: Globe,
      title: 'Jangkauan Luas',
      description: 'Melayani seluruh wilayah Sulawesi dengan infrastruktur terbaik.',
      color: 'blue',
      size: 'normal'
    },
    {
      icon: Headphones,
      title: 'Dukungan 24/7',
      description: 'Tim teknis siap membantu Anda kapan saja.',
      color: 'blue',
      size: 'wide',
      image: 'https://images.unsplash.com/photo-1766066014237-00645c74e9c6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNTl8MHwxfHNlYXJjaHwxfHxjdXN0b21lciUyMHN1cHBvcnQlMjBhZ2VudCUyMGhlYWRzZXQlMjBzbWlsaW5nJTIwbW9kZXJuJTIwb2ZmaWNlfGVufDB8fHx8MTc3MjU0ODQ5N3ww&ixlib=rb-4.1.0&q=85'
    },
    {
      icon: Gamepad2,
      title: 'Gaming Ready',
      description: 'Latency rendah untuk pengalaman gaming tanpa lag.',
      color: 'red',
      size: 'normal'
    },
    {
      icon: Tv,
      title: 'Streaming 4K',
      description: 'Tonton konten favorit dalam kualitas ultra HD.',
      color: 'blue',
      size: 'normal'
    },
    {
      icon: Building2,
      title: 'Solusi Bisnis',
      description: 'Paket khusus untuk kebutuhan korporat dan UMKM.',
      color: 'red',
      size: 'normal'
    },
    {
      icon: Wifi,
      title: 'WiFi Gratis',
      description: 'Router WiFi berkualitas tinggi gratis untuk pelanggan.',
      color: 'blue',
      size: 'normal'
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5 }
    }
  };

  return (
    <section 
      id="layanan" 
      className="py-24 md:py-32 bg-[#0A0A0A]"
      data-testid="services-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Section Header */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="text-sm font-medium tracking-widest uppercase text-brand-blue mb-4 block">
            Layanan Kami
          </span>
          <h2 className="font-outfit text-4xl md:text-5xl font-bold tracking-tight text-white mb-6">
            Internet <span className="gradient-text-red">Premium</span> untuk Semua
          </h2>
          <p className="text-lg text-gray-400 max-w-2xl mx-auto">
            Solusi konektivitas lengkap untuk rumah tangga, bisnis, dan kebutuhan khusus Anda.
          </p>
        </motion.div>

        {/* Bento Grid */}
        <motion.div 
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid grid-cols-1 md:grid-cols-4 gap-6"
        >
          {services.map((service, index) => {
            const Icon = service.icon;
            const isLarge = service.size === 'large';
            const isWide = service.size === 'wide';
            
            return (
              <motion.div
                key={index}
                variants={itemVariants}
                className={`
                  service-card bg-[#121212] rounded-xl p-6 relative overflow-hidden
                  ${isLarge ? 'md:col-span-2 md:row-span-2' : ''}
                  ${isWide ? 'md:col-span-2' : ''}
                  ${!isLarge && !isWide ? 'md:col-span-1' : ''}
                `}
                data-testid={`service-card-${index}`}
              >
                {/* Background Image for large/wide cards */}
                {service.image && (isLarge || isWide) && (
                  <div className="absolute inset-0 z-0">
                    <img 
                      src={service.image} 
                      alt={service.title}
                      className="w-full h-full object-cover opacity-30"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#121212] via-[#121212]/80 to-transparent" />
                  </div>
                )}

                <div className={`relative z-10 ${isLarge ? 'h-full flex flex-col justify-end' : ''}`}>
                  {/* Icon */}
                  <div className={`
                    icon-container mb-4
                    ${service.color === 'red' ? 'icon-container-red' : ''}
                  `}>
                    <Icon className={`w-6 h-6 ${service.color === 'red' ? 'text-brand-red' : 'text-brand-blue'}`} />
                  </div>

                  {/* Title */}
                  <h3 className={`
                    font-outfit font-semibold text-white mb-2
                    ${isLarge ? 'text-2xl md:text-3xl' : 'text-xl'}
                  `}>
                    {service.title}
                  </h3>

                  {/* Description */}
                  <p className={`
                    text-gray-400 leading-relaxed
                    ${isLarge ? 'text-base md:text-lg' : 'text-sm'}
                  `}>
                    {service.description}
                  </p>
                </div>
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
};

export default ServicesSection;
